// hooks/useCartActions.ts
import { Productbtn } from "../../../../../../shared/services/interfacev2";

interface CartActionsParams {
  selectedProductslist: Productbtn[];
  setSelectedProductslist: React.Dispatch<React.SetStateAction<Productbtn[]>>;
  setValueMap: React.Dispatch<React.SetStateAction<{ [id: string]: number }>>;
  setCondition: React.Dispatch<React.SetStateAction<number>>;
  setEditedAmounts?: React.Dispatch<React.SetStateAction<{ [productId: string]: string }>>;
  editedAmounts?: { [productId: string]: string };
}

export const useCartActions = ({
  selectedProductslist,
  setSelectedProductslist,
  setValueMap,
  setCondition,
  setEditedAmounts,
}: CartActionsParams) => {
  // ✅ Add product
  const addProductToCart = (product: Productbtn) => {
    // Identify uniqueness by both _id and Amount
    const key = `${product._id.$oid}-${product.amount}`;
    
    setValueMap((prev) => {
      const currentQuantity = prev[key] || 0;
      const newQuantity = currentQuantity + 1;
      
      // Add product to list if not present
      if (currentQuantity === 0) {
        setSelectedProductslist((prevList) => [...prevList, product]);
      }
      
      return {
        ...prev,
        [key]: newQuantity,
      };
    });
  };

  const removeProductFromCart = (productId: string, amount: string) => {
    const key = `${productId}-${amount}`;
    
    // First, get the current valueMap state to check if product has other quantities
    let hasOtherQuantities = false;
    
    setValueMap((prev) => {
      const newMap = { ...prev };
      delete newMap[key];
      
      // Check if this product ID still exists in the map with other amounts
      hasOtherQuantities = Object.keys(newMap).some(
        (k) => k.startsWith(productId)
      );
      
      return newMap;
    });

    // If no other quantities exist for this product, remove it from the list
    if (!hasOtherQuantities) {
      setSelectedProductslist((prev) =>
        prev.filter((p) => p._id.$oid !== productId)
      );
      
      // Also remove any edited amount for this product
      if (setEditedAmounts) {
        setEditedAmounts(prev => {
          const newMap = { ...prev };
          delete newMap[productId];
          return newMap;
        });
      }
    }
  };

  const updateQuantity = (
    productId: string,
    amount: string,
    type: "add" | "subtract"
  ) => {
    const key = `${productId}-${amount}`;
    
    setValueMap((prev) => {
      const currentQuantity = prev[key] || 0;
      let newQuantity = currentQuantity;

      if (type === "add") {
        newQuantity = currentQuantity + 1;
      } else {
        newQuantity = Math.max(0, currentQuantity - 1);
      }

      if (newQuantity === 0) {
        const newMap = { ...prev };
        delete newMap[key];
        return newMap;
      }

      return {
        ...prev,
        [key]: newQuantity,
      };
    });
  };

  return {
    addProductToCart,
    removeProductFromCart,
    updateQuantity,
  };
};